import { useState, useCallback, lazy, Suspense, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';
import HeroSection from '@/components/home/HeroSection';
import { booksService } from '@/services/booksService';

// Defer below-the-fold sections to shrink initial bundle/TBT on mobile
const WhyTwiceRead = lazy(() => import('@/components/home/WhyTwiceRead'));
const HowItWorks = lazy(() => import('@/components/home/HowItWorks'));
const FeaturedBooks = lazy(() => import('@/components/home/FeaturedBooks'));
const CTASection = lazy(() => import('@/components/home/CTASection'));
const Footer = lazy(() => import('@/components/Footer'));

const SectionFallback = () => <div className="min-h-[200px]" aria-hidden="true" />;

const Index = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const { data: featuredBooks = [], isLoading, isError } = useQuery({
    queryKey: ['featuredBooks', 8],
    queryFn: () => booksService.getFeaturedBooks(8),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleSearch = useCallback((e: FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  }, [searchQuery, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Twice Read – Buy & Sell Used Books Online in India"
        description="India's trusted marketplace for pre-loved books. Browse textbooks, novels, and rare finds at up to 50% off. Secure payments and fast shipping."
        canonicalPath="/"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Twice Read',
          url: 'https://twiceread.store',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://twiceread.store/search?search={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <Navbar />
      <main id="main-content">
        <HeroSection
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearch={handleSearch}
        />
        <Suspense fallback={<SectionFallback />}>
          <WhyTwiceRead />
          <HowItWorks />
          <FeaturedBooks books={featuredBooks} loading={isLoading} error={isError} />
          <CTASection />
        </Suspense>
      </main>
      <Suspense fallback={<SectionFallback />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
